import {
  AttachFileOutlined,
  FileUploadOutlined,
  HouseOutlined,
  PersonOutline,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
} from "@mui/material";
import Button from "@mui/material/Button";
import { grey } from "@mui/material/colors";
import { isEmpty } from "lodash";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import EnhancedCommentAttachmentPreview from "../../../_src_shared/comments/EnhancedCommentAttachmentPreview";
import RenderedPreviewUploadedFiles from "../../../_src_shared/comments/components/renderers/RenderedPreviewUploadedFiles";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { loanPipelineClear } from "../../../redux/actions/loanPipelineActions";
import SearchInputLight from "../../inputs/SearchInputLight";
import Flx from "../../layout/Flx";
import { useChecklistRouteHook } from "../../../hooks/useChecklistRouteHook";
import HtmlEditor from "../../html/HtmlEditor";
import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";

const onSuccessActionEnums = {
  LOAN_DRILLDOWN: "loanDrilldown",
  LOAN_PIPELINE: "loanPipeline",
};

const LoanEnhancedCommentTextBox = ({
  loan,
  onSuccessfn,
  onSuccessAction,
  borrowerIds,
  submitType = "LOAN",
  subjectPropertyIds,
  documentIds,
  sx,
  docGroup,
  docType,
}) => {
  // Keep attachmentIds separate from uploadedFiles
  const attachmentsInitialState = useMemo(() => {
    return {
      borrowers: borrowerIds || [],
      subjectProperties: subjectPropertyIds || [],
      loanDocuments: documentIds || [],
    };
  }, [borrowerIds, subjectPropertyIds, documentIds]);

  /**
   * LOCAL STATE
   */
  const [attachmentIds, setAttachmentIds] = useState(attachmentsInitialState);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [comment, setComment] = useState(null);
  // Bumped after a successful submit to force-remount the uncontrolled HtmlEditor
  // so its internal content clears.
  const [editorKey, setEditorKey] = useState(0);
  /**
   * CALCULATED STATE
   */
  const showCommentFooter = useMemo(() => {
    if (isNil(comment)) {
      return false;
    }

    if (comment?.plainText?.trim()) {
      return true;
    }

    // Also show footer if there are uploaded files
    if (uploadedFiles.length > 0) {
      return true;
    }

    return false;
  }, [comment, uploadedFiles]);

  const fileInputRef = useRef(null);
  const { createEnhancedDealComment, createDealComment, loading } =
    useUnderwritingHook();
  const {
    checklistRouteLoading,
    createLoanUnderwritingRequirement,
    createLoanUnderwritingRequirementWithFile,
  } = useChecklistRouteHook();
  const dispatch = useDispatch();

  // Global paste handler - handles paste anywhere on the page when this component is mounted
  useEffect(() => {
    const handlePaste = (e) => {
      // Check if the paste event contains files/images
      const items = e.clipboardData?.items;
      if (!items) return;

      let hasImage = false;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith("image/")) {
          hasImage = true;
          const file = item.getAsFile();
          if (file) {
            // Prevent default only if we're handling an image
            e.preventDefault();
            e.stopPropagation();
            handleImageFile(file);
          }
        }
      }

      // Also check for files in the clipboardData
      const files = e.clipboardData?.files;
      if (files && files.length > 0 && !hasImage) {
        e.preventDefault();
        e.stopPropagation();
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (file.type.startsWith("image/")) {
            handleImageFile(file);
          } else {
            handleRegularFile(file);
          }
        }
      }
    };

    // Add listener to document to catch all paste events
    document.addEventListener("paste", handlePaste, true);

    return () => {
      document.removeEventListener("paste", handlePaste, true);
    };
  }, [uploadedFiles, docGroup, docType]);

  useEffect(() => {
    setAttachmentIds({
      borrowers: borrowerIds || [],
      subjectProperties: subjectPropertyIds || [],
      loanDocuments: documentIds || [],
    });
  }, [borrowerIds, subjectPropertyIds, documentIds]);

  /**
   * FUNCTIONS
   */
  const onSubmit = () => {
    // Build base payload
    const payload = {
      htmlString: comment?.htmlString,
      associations: attachmentIds,
      // ...attachmentIds,
    };

    const requestParams = {
      loanId: loan?._id,
      dealId: loan?._id,
      data: payload,
      onSuccessFn: (response) => {
        if (isFunction(onSuccessfn)) {
          onSuccessfn(response);
        }

        // Always reset local editor state after a successful submit,
        // regardless of which action branch we take afterwards.
        setComment(null);
        setAttachmentIds(attachmentsInitialState);
        setUploadedFiles([]);
        setEditorKey((k) => k + 1);

        if (onSuccessAction === "custom") {
          return;
        }
        if (onSuccessAction === onSuccessActionEnums.LOAN_DRILLDOWN) {
          dispatch(loanDrilldownSet(response));
          return;
        }

        if (onSuccessAction === onSuccessActionEnums.LOAN_PIPELINE) {
          dispatch(loanPipelineClear(response));
          return;
        }

        dispatch(loanDrilldownSet(response));
        dispatch(loanPipelineClear(response));
      },
      onSuccessMsg: "Comment created successfully",
    };

    if (isEmpty(uploadedFiles)) {
      if (submitType === "uwRequirements") {
        createLoanUnderwritingRequirement(requestParams);
        return;
      }
      createDealComment(requestParams);
    } else {
      payload.uploadMetadata = uploadedFiles.map((fileObj) => ({
        id: fileObj.id,
        file: fileObj.file,
        name: fileObj.name,
        file_display_name: fileObj.file_display_name,
        docGroup: fileObj.docGroup,
        docType: fileObj.docType,
      }));
      if (submitType === "uwRequirements") {
        createLoanUnderwritingRequirementWithFile(requestParams);
        return;
      }
      createEnhancedDealComment(requestParams);
    }
  };

  // Helper functions to update specific ID arrays
  const addAttachmentIdByKey = (newId, name) => {
    setAttachmentIds((prev) => ({ ...prev, [name]: [...prev[name], newId] }));
  };

  const deleteAttachmentIdByKey = (id, name) => {
    setAttachmentIds((prev) => ({
      ...prev,
      [name]: prev[name].filter((attachmentId) => attachmentId !== id),
    }));
  };

  // Delete uploaded file
  const deleteUploadedFile = (id) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // Handle file upload from file input
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach((file) => {
      if (file.type.startsWith("image/")) {
        handleImageFile(file);
      } else {
        handleRegularFile(file);
      }
    });
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Process image file (matching FileUploadField structure)
  const handleImageFile = (file) => {
    // Generate a filename if it doesn't have one (for pasted images)
    const fileName = file.name || `pasted-image-${Date.now()}.png`;

    const reader = new FileReader();
    reader.onloadend = () => {
      const newFile = {
        id: uuidv4(), // Use uuid like FileUploadField
        file: file,
        name: fileName,
        file_display_name: fileName,
        docGroup: docGroup || undefined,
        docType: docType || undefined,
        // Additional properties for display
        size: file.size,
        type: file.type || "image/png",
        preview: reader.result, // Base64 preview for images
        isImage: true,
      };
      setUploadedFiles((prev) => [...prev, newFile]);
    };
    reader.readAsDataURL(file);
  };

  // Process regular file (matching FileUploadField structure)
  const handleRegularFile = (file) => {
    const newFile = {
      id: uuidv4(), // Use uuid like FileUploadField
      file: file,
      name: file.name,
      file_display_name: file.name,
      docGroup: docGroup || undefined,
      docType: docType || undefined,
      // Additional properties for display
      size: file.size,
      type: file.type,
      isImage: false,
    };
    setUploadedFiles((prev) => [...prev, newFile]);
  };

  const updateUploadedFileName = (id, newName) => {
    setUploadedFiles((prev) =>
      prev.map((file) =>
        file.id === id ? { ...file, file_display_name: newName } : file,
      ),
    );
  };

  const updateUploadedFileDocGroupType = (id, newDocGroup, newDocType) => {
    setUploadedFiles((prev) =>
      prev.map((file) =>
        file.id === id
          ? { ...file, docGroup: newDocGroup, docType: newDocType }
          : file,
      ),
    );
  };

  const styles = useMemo(() => {
    return {
      border: "1px solid #dde0e4",
      borderRadius: "6px",
      overflow: "hidden",
      position: "relative",
      ...sx,
    };
  }, [sx]);

  return (
    <Flx column fw gap={0} sx={styles}>
      <Box sx={{ position: "relative" }}>
        <HtmlEditor
          key={editorKey}
          value={comment}
          suppressOuterBorder
          onChange={setComment}
          placeholder="Start typing to add new comment... (Or paste images directly with Ctrl+V)"
        />
        {/* <HtmlTextEditor
          value={comment}
          suppressOuterBorder
          onChange={setComment}
          placeholder="Start typing to add new comment... (Or paste images directly with Ctrl+V)"
        /> */}
        {/* Invisible paste target that covers the editor area */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: -1,
          }}
          tabIndex={-1}
        />
      </Box>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
        style={{ display: "none" }}
        onChange={handleFileUpload}
      />

      <Flx wrap gap={1} jb fw sx={{ p: 1 }}>
        <AttachmentUtilsRow
          loan={loan}
          attachmentIds={attachmentIds}
          onAdd={addAttachmentIdByKey}
          onFileUploadClick={() => fileInputRef.current?.click()}
        />

        <Button
          loading={loading || checklistRouteLoading}
          disabled={!showCommentFooter}
          onClick={onSubmit}
          size="small"
        >
          Submit
        </Button>
      </Flx>

      <EnhancedCommentAttachmentPreview
        loan={loan}
        //
        borrowerIds={attachmentIds?.borrowers}
        loanDocumentIds={attachmentIds?.loanDocuments}
        subjectPropertyIds={attachmentIds?.subjectProperties}
        //
        uploadedFiles={uploadedFiles}
        onDeleteAttachment={deleteAttachmentIdByKey}
        onDeleteUploadedFile={deleteUploadedFile}
        sx={{ borderTop: "1px solid #dde0e4", p: 1, background: grey[50] }}
      >
        <RenderedPreviewUploadedFiles
          files={uploadedFiles}
          onDelete={deleteUploadedFile}
          onUpdateFileName={updateUploadedFileName}
          onUpdateDocGroupType={updateUploadedFileDocGroupType}
        />
      </EnhancedCommentAttachmentPreview>
    </Flx>
  );
};

const AttachmentUtilsRow = ({
  loan,
  attachmentIds,
  onAdd,
  onFileUploadClick,
}) => {
  return (
    <Flx gap={1}>
      <AttachmentUtilButton
        tooltip="Upload New File (Images, PDFs, Documents)"
        icon={<FileUploadOutlined className="thin" />}
        onClick={onFileUploadClick}
      />
      {isEmpty(loan?.loanDocuments) ? null : (
        <AttachmentUtilDropdownButton
          tooltip="Select Existing File"
          icon={<AttachFileOutlined className="thin" />}
          enableSearch
          options={loan?.loanDocuments?.map((b) => {
            return {
              _id: b._id,
              label: b?.file_display_name,
              icon: <AttachFileOutlined className="thin" />,
            };
          })}
          onClick={(id) => onAdd(id, "loanDocuments")}
          attachmentIds={attachmentIds}
          attachmentKey="loanDocuments"
        />
      )}

      <AttachmentUtilDropdownButton
        tooltip="Tag/Assign Borrower"
        icon={<PersonOutline className="thin" />}
        options={loan?.borrowers?.map((b) => {
          return {
            _id: b._id,
            label: `${b.firstName || ""} ${b.lastName || ""}`,
            icon: <PersonOutline className="thin" />,
          };
        })}
        onClick={(id) => onAdd(id, "borrowers")}
        attachmentIds={attachmentIds}
        attachmentKey="borrowers"
      />

      <AttachmentUtilDropdownButton
        tooltip="Tag/Assign Property"
        icon={<HouseOutlined className="thin" />}
        options={loan?.subjectProperties?.map((b) => {
          return {
            _id: b._id,
            label: addressLabel(b?.address),
            icon: <HouseOutlined className="thin" />,
          };
        })}
        onClick={(id) => onAdd(id, "subjectProperties")}
        attachmentIds={attachmentIds}
        attachmentKey="subjectProperties"
      />
    </Flx>
  );
};

/**
 ****************
 * Shared Components
 ****************
 */

const AttachmentUtilDropdownButton = ({
  tooltip,
  icon,
  onClick,
  disabled,
  options,
  enableSearch,
  attachmentIds,
  attachmentKey,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (!options || options.length === 0) {
      if (isFunction(onClick)) {
        onClick();
      }
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchValue("");
  };

  const handleMenuItemClick = (option) => {
    if (isFunction(onClick)) {
      onClick(option._id);
    }
    setAnchorEl(null);
    setSearchValue("");
  };

  // Filter options based on search and already selected items
  const filteredOptions = useMemo(() => {
    if (!options) return [];

    let filtered = options;

    // Filter out already selected items
    if (attachmentIds && attachmentKey && attachmentIds[attachmentKey]) {
      filtered = filtered.filter(
        (option) => !attachmentIds[attachmentKey].includes(option._id),
      );
    }

    // Filter based on search value
    if (enableSearch && searchValue && searchValue.trim()) {
      const searchTerm = searchValue.toLowerCase().trim();
      filtered = filtered.filter((option) =>
        option.label.toLowerCase().includes(searchTerm),
      );
    }

    return filtered;
  }, [options, searchValue, enableSearch, attachmentIds, attachmentKey]);

  return (
    <>
      <AttachmentUtilButton
        tooltip={tooltip}
        icon={icon}
        onClick={handleClick}
        disabled={disabled}
      />

      {options && options.length > 0 && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          sx={{
            "& .MuiPopover-paper": {
              mt: 1,
              border: "1px solid #dde0e4",
              borderRadius: "4px",
              minWidth: "200px",
              maxWidth: "400px",
            },
          }}
        >
          <Box sx={{ width: "100%" }}>
            {enableSearch && (
              <Box
                sx={{
                  px: 1.4,
                  py: 1,
                  borderBottom: "1px solid #dde0e4",
                }}
              >
                <SearchInputLight
                  value={searchValue}
                  onChange={setSearchValue}
                  placeholder="Search..."
                  autoFocus
                  onKeyDown={(e) => {
                    // Prevent closing popover on escape while searching
                    if (e.key === "Escape") {
                      e.stopPropagation();
                      setSearchValue("");
                    }
                  }}
                />
              </Box>
            )}

            <List
              sx={{
                maxHeight: "400px",
                overflowY: "auto",
                py: 0.5,
              }}
              disablePadding
            >
              {filteredOptions.length === 0 ? (
                <ListItem
                  sx={{
                    pl: 1.4,
                    fontStyle: "italic",
                    color: grey[600],
                    cursor: "default",
                  }}
                >
                  <ListItemText
                    primary="No options available"
                    primaryTypographyProps={{ fontSize: "12px" }}
                  />
                </ListItem>
              ) : (
                filteredOptions.map((option, index) => (
                  <ListItem
                    key={option._id || index}
                    onClick={() => handleMenuItemClick(option)}
                    sx={{
                      pl: 1.4,
                      pr: 1.4,
                      py: 0.5,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: grey[100],
                      },
                      ".MuiListItemIcon-root": {
                        minWidth: "26px",
                        ".MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      },
                    }}
                  >
                    {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
                    <ListItemText
                      primary={option.label}
                      primaryTypographyProps={{ fontSize: "11px" }}
                    />
                    {option.endIcon && (
                      <ListItemIcon>{option.endIcon}</ListItemIcon>
                    )}
                  </ListItem>
                ))
              )}
            </List>
          </Box>
        </Popover>
      )}
    </>
  );
};

const AttachmentUtilButton = ({ tooltip, icon, onClick, disabled }) => {
  return (
    <Tooltip title={tooltip}>
      <IconButton
        onClick={onClick}
        disabled={disabled}
        size="small"
        variant="text"
        sx={{
          borderRadius: "8px",
          border: "1px solid #dde0e4",
          ".MuiButton-icon": {
            mr: 0.2,
          },
          ".MuiSvgIcon-root": {
            fontSize: "14px !important",
          },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
};

export default LoanEnhancedCommentTextBox;
